import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Signature, UploadSimple, Trash, Info } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { useAuth, formatApiErrorDetail } from '@/auth/AuthContext';
import { formatDateTime } from '@/utils/datetime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Common Japanese hanko diameters (mm) shown as reference squares on the preview.
const STAMP_REFS = [
  { mm: 10, color: '#10b981' },
  { mm: 12, color: '#f59e0b' },
  { mm: 18, color: '#71717a' },
];

export default function SignatureSettings() {
  const { user } = useAuth();
  const fileRef = useRef(null);
  const [meta, setMeta] = useState({ has_signature: false, updated_at: null });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [bust, setBust] = useState(Date.now());
  const [mm, setMm] = useState(15);
  const [savingSize, setSavingSize] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/signatures/me`);
      setMeta(r.data);
      if (r.data.default_mm) setMm(r.data.default_mm);
      setBust(Date.now());
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onPick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/jpg', 'image/webp'].includes(file.type)) {
      toast.error('PNG / JPG / WEBP 画像を選択してください');
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast.error('ファイルサイズは4MB以下にしてください');
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    try {
      setUploading(true);
      await axios.post(`${API}/signatures/me`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('署名を保存しました');
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'アップロードに失敗しました');
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async () => {
    try {
      await axios.delete(`${API}/signatures/me`);
      toast.success('署名を削除しました');
      await load();
    } catch (err) {
      toast.error('削除に失敗しました');
    }
  };

  const saveSize = async () => {
    try {
      setSavingSize(true);
      await axios.put(`${API}/signatures/me/default-size`, { mm });
      toast.success('デフォルトサイズを保存しました');
    } catch (err) {
      toast.error('保存に失敗しました');
    } finally {
      setSavingSize(false);
    }
  };


  return (
    <div className="max-w-2xl" data-testid="signature-settings-page">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-zinc-900 flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-teal-700 text-white">
            <Signature size={18} weight="duotone" />
          </span>
          署名・捺印
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          申請書を検証・承認する際に使う署名（印影）画像を登録します。背景が透過したPNGがおすすめです。
        </p>
      </div>

      <div className="bg-white rounded-xl border border-zinc-200 p-6">
        <div className="flex flex-col sm:flex-row gap-6 items-start">
          <div
            className="w-40 h-40 shrink-0 rounded-lg border-2 border-dashed border-zinc-200 bg-[linear-gradient(45deg,#f8fafc_25%,transparent_25%),linear-gradient(-45deg,#f8fafc_25%,transparent_25%),linear-gradient(45deg,transparent_75%,#f8fafc_75%),linear-gradient(-45deg,transparent_75%,#f8fafc_75%)] bg-[length:16px_16px] bg-[position:0_0,0_8px,8px_-8px,-8px_0] flex items-center justify-center overflow-hidden"
            data-testid="signature-preview"
          >
            {loading ? (
              <span className="text-xs text-zinc-400">読み込み中...</span>
            ) : meta.has_signature ? (
              <img
                src={`${API}/signatures/user/${user.id}?t=${bust}`}
                alt="署名"
                className="max-w-full max-h-full object-contain"
                data-testid="signature-image"
              />
            ) : (
              <span className="text-xs text-zinc-400 text-center px-2">署名未登録</span>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onFile} className="hidden" data-testid="signature-file-input" />
            <div className="flex flex-wrap gap-2">
              <Button onClick={onPick} disabled={uploading} data-testid="signature-upload-btn" className="bg-teal-700 hover:bg-teal-800">
                <UploadSimple size={16} className="mr-2" />
                {uploading ? 'アップロード中...' : meta.has_signature ? '署名を変更' : '署名をアップロード'}
              </Button>
              {meta.has_signature && (
                <Button onClick={onDelete} variant="outline" data-testid="signature-delete-btn" className="text-red-600 border-red-200 hover:bg-red-50">
                  <Trash size={16} className="mr-2" />
                  削除
                </Button>
              )}
            </div>
            {meta.updated_at && (
              <p className="text-[11px] text-zinc-400 mt-2">
                最終更新: {formatDateTime(meta.updated_at)}
              </p>
            )}
            <div className="mt-4 flex items-start gap-2 text-[12px] text-zinc-500 bg-zinc-50 rounded-md p-3">
              <Info size={16} className="shrink-0 mt-0.5 text-zinc-400" />
              <span>
                登録後、申請履歴や申請の詳細から「検証」を押すと、申請書に署名・テキスト・日付を配置して承認できます。
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl p-5 mt-4 max-w-2xl">
        <h3 className="text-sm font-bold text-zinc-900">署名印のデフォルトサイズ</h3>
        <p className="text-[12px] text-zinc-500 mt-1">
          検証画面で署名・印を追加したときの初期サイズです。印鑑は正方形で、一般的な10〜18mmから選べます。用紙サイズが違っても実寸（mm）は同じになります。配置後も個別に拡大・縮小できます。
        </p>

        <div className="flex items-center gap-6 mt-4">
          <div className="flex-1">
            <label className="text-xs text-zinc-600">印鑑サイズ: <span className="font-semibold text-zinc-900">{mm.toFixed(1)}mm</span> <span className="text-zinc-400">（正方形）</span></label>
            <input type="range" min="10" max="18" step="0.5" value={mm}
              onChange={(e) => setMm(parseFloat(e.target.value))}
              data-testid="sig-default-mm" className="mt-1.5 w-full block" />
            <div className="flex justify-between text-[10px] text-zinc-400 mt-0.5">
              <span>10mm</span><span>18mm</span>
            </div>

            <div className="mt-4">
              <p className="text-[11px] text-zinc-500 mb-1.5">よく使う印鑑サイズに合わせる</p>
              <div className="flex flex-wrap gap-1.5">
                {STAMP_REFS.map((ref) => (
                  <button key={ref.mm} type="button" onClick={() => setMm(ref.mm)}
                    data-testid={`sig-ref-btn-${ref.mm}`}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs transition-colors ${
                      Math.abs(mm - ref.mm) < 0.25
                        ? 'border-teal-700 bg-teal-700/5 text-teal-700'
                        : 'border-zinc-200 text-zinc-700 hover:bg-zinc-50'
                    }`}>
                    <span className="inline-block w-2.5 h-2.5 rounded-sm border border-dashed" style={{ borderColor: ref.color }} />
                    {ref.mm}mm
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Close-up preview — the stamp + dashed reference squares (10/12/18mm) to scale */}
          <div className="shrink-0 relative bg-zinc-50 border border-zinc-200 rounded-md"
               style={{ width: 124, height: 124 }}
               data-testid="sig-default-preview">
            {STAMP_REFS.map((ref) => (
              <div key={ref.mm} aria-hidden
                style={{
                  position: 'absolute', left: '50%', top: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: ref.mm * 6, height: ref.mm * 6,
                  border: `1px dashed ${ref.color}`, borderRadius: 2,
                }}
                data-testid={`sig-ref-square-${ref.mm}`}
              />
            ))}
            <div
              className="absolute flex items-center justify-center overflow-hidden"
              style={{
                left: '50%', top: '50%', transform: 'translate(-50%, -50%)',
                width: mm * 6, height: mm * 6,
              }}
              data-testid="sig-default-preview-stamp"
            >
              {meta.has_signature
                ? <img src={`${API}/signatures/user/${user.id}?t=${bust}`} alt="" className="w-full h-full object-contain" />
                : <span className="w-full h-full flex items-center justify-center border-2 border-solid border-rose-400 bg-rose-50/70 rounded text-[10px] font-semibold text-rose-500">印</span>}
            </div>
          </div>
        </div>

        <Button onClick={saveSize} disabled={savingSize}
          data-testid="sig-default-save"
          className="mt-4 bg-teal-700 hover:bg-teal-800">
          {savingSize ? '保存中...' : 'サイズを保存'}
        </Button>
      </div>
    </div>
  );
}
